import { useCallback, useMemo, useState } from 'react'
import { parseAllDocuments } from 'yaml'
import { Button } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { useRouterState } from '@tanstack/react-router'
import {
  numberLocale,
  pilotAnalyticsDimensions,
  useLocale,
  useT,
} from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'yaml-formatter'

interface YamlParseIssue extends Error {
  code?: string
  linePos?: Array<{
    line: number
    col: number
  }>
  pos?: [number, number]
}

type YamlResult = { ok: true; result: string } | { ok: false; error: string }
type ValidationResult = { ok: true } | { ok: false; error: string }

function lineColumnFromOffset(input: string, offset: number): { line: number; col: number } {
  const lines = input.slice(0, offset).split('\n')
  return {
    line: lines.length,
    col: lines[lines.length - 1]!.length + 1,
  }
}

function formatYamlIssue(issue: unknown, input?: string): string {
  if (!(issue instanceof Error)) return String(issue)

  const yamlIssue = issue as YamlParseIssue
  const location = yamlIssue.linePos?.[0]
    ?? (input && typeof yamlIssue.pos?.[0] === 'number'
      ? lineColumnFromOffset(input, yamlIssue.pos[0])
      : undefined)
  const prefix = [
    location ? `line ${location.line}, column ${location.col}` : null,
    yamlIssue.code ? yamlIssue.code : null,
  ]
    .filter(Boolean)
    .join(' ')

  return prefix ? `${prefix}: ${issue.message}` : issue.message
}

function collectYamlError(input: string): string | null {
  const documents = parseAllDocuments(input, { prettyErrors: false })
  const firstError = documents.flatMap((document) => document.errors)[0]
  return firstError ? formatYamlIssue(firstError, input) : null
}

function formatYaml(input: string): YamlResult {
  if (!input.trim()) return { ok: false, error: 'empty' }

  try {
    const documents = parseAllDocuments(input, { prettyErrors: false })
    const firstError = documents.flatMap((document) => document.errors)[0]
    if (firstError) return { ok: false, error: formatYamlIssue(firstError, input) }

    const result = documents
      .map((document) => document.toString({ indent: 2, lineWidth: 0 }))
      .join('')
      .trimEnd()

    return { ok: true, result: result ? `${result}\n` : '' }
  } catch (error) {
    return { ok: false, error: formatYamlIssue(error, input) }
  }
}

function validateYaml(input: string): ValidationResult {
  if (!input.trim()) return { ok: false, error: 'empty' }

  try {
    const error = collectYamlError(input)
    return error ? { ok: false, error } : { ok: true }
  } catch (error) {
    return { ok: false, error: formatYamlIssue(error, input) }
  }
}

export function YamlFormatterPage() {
  const t = useT()
  const { locale } = useLocale()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const localeCode = numberLocale(locale)
  const analyticsDimensions = useMemo(
    () => pilotAnalyticsDimensions(pathname, locale),
    [locale, pathname],
  )
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [validationMessage, setValidationMessage] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const inputStats = useMemo(() => {
    const chars = input.length
    const lines = input ? input.split('\n').length : 0
    return { chars, lines }
  }, [input])

  const outputStats = useMemo(() => {
    const chars = output.length
    const lines = output ? output.split('\n').length : 0
    return { chars, lines }
  }, [output])

  const handleFormat = useCallback(() => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setValidationMessage(null)
      return
    }

    const start = performance.now()
    setValidationMessage(null)
    toolInvoke(UTILITY, { action: 'format', ...analyticsDimensions })

    const result = formatYaml(input)
    if (result.ok) {
      setOutput(result.result)
      setError(null)
      setValidationMessage(t.yamlFormatter.valid)
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        action: 'format',
        ...analyticsDimensions,
      })
    } else {
      setOutput('')
      setError(t.yamlFormatter.errors.invalidYaml.replace('{error}', result.error))
      setValidationMessage(null)
      toolError(UTILITY, 'invalid-yaml', { action: 'format', ...analyticsDimensions })
    }
  }, [analyticsDimensions, input, t])

  const handleValidate = useCallback(() => {
    if (!input.trim()) {
      setValidationMessage(null)
      setError(null)
      setOutput('')
      return
    }

    const start = performance.now()
    setOutput('')
    toolInvoke(UTILITY, { action: 'validate', ...analyticsDimensions })

    const result = validateYaml(input)
    if (result.ok) {
      setValidationMessage(t.yamlFormatter.valid)
      setError(null)
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        action: 'validate',
        ...analyticsDimensions,
      })
    } else {
      setValidationMessage(null)
      setError(t.yamlFormatter.errors.invalidYaml.replace('{error}', result.error))
      toolError(UTILITY, 'invalid-yaml', { action: 'validate', ...analyticsDimensions })
    }
  }, [analyticsDimensions, input, t])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
    setValidationMessage(null)
  }, [])

  return (
    <ToolPageShell toolKey="yamlFormatter" width="wide">
      <CodeWorkspace
        error={error}
        notice={validationMessage}
        controls={(
          <div className="flex flex-wrap items-center gap-3">
            <Button data-code-run onClick={handleFormat} disabled={!input.trim()}>
              {t.yamlFormatter.format}
            </Button>
            <Button data-code-run variant="outline" onClick={handleValidate} disabled={!input.trim()}>
              {t.yamlFormatter.validate}
            </Button>
          </div>
        )}
        input={{
          title: t.yamlFormatter.input,
          value: input,
          onChange: (value) => {
            setInput(value)
            setError(null)
            setValidationMessage(null)
          },
          placeholder: t.yamlFormatter.inputPlaceholder,
          ariaLabel: t.yamlFormatter.input,
          clear: { label: t.yamlFormatter.clear, onClick: handleClear },
          stats: [
            {
              label: t.yamlFormatter.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.yamlFormatter.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.yamlFormatter.output,
          value: output,
          readOnly: true,
          placeholder: t.yamlFormatter.outputPlaceholder,
          ariaLabel: t.yamlFormatter.output,
          actions: (
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? t.yamlFormatter.copied : t.yamlFormatter.copy}
            </Button>
          ),
          stats: output && !error
            ? [
              {
                label: t.yamlFormatter.characters,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.yamlFormatter.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.yamlFormatter.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.yamlFormatter.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.yamlFormatter.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.yamlFormatter.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
